import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  ChevronDown,
  ChevronRight,
  Layers,
  Loader2,
  MapPin,
  Network,
  RefreshCw,
  Search,
  Users,
} from "lucide-react";
import { AuthContext } from "../Context/AuthContext";
import { hasRole } from "../../rbac/access";
import { normalizeApiError } from "../../utils/normalizeApiError";
import { formatRoleLabel, normalizeEmployeeRole } from "./employeeRoleConstants";
import { getRoleTheme } from "./hierarchyLayerConfig";
import { getOrgLocationStructure } from "./hierarchyApi";
import PeoplePageShell from "./PeoplePageShell";
import { PEOPLE_PATHS } from "./peopleModules";

function StatPill({ icon: Icon, label, value }) {
  return (
    <div className="ols-stat">
      <span className="ols-stat-icon" aria-hidden>
        <Icon className="w-3.5 h-3.5" />
      </span>
      <div>
        <strong>{value}</strong>
        <em>{label}</em>
      </div>
    </div>
  );
}

function EmployeeChip({ emp }) {
  const theme = getRoleTheme(emp.role);
  return (
    <Link
      to={`/employees/${emp.employee_id}`}
      className="ols-emp-chip"
      style={{ borderColor: theme.border, background: theme.bg, color: theme.text }}
      title={`${emp.employee_name} · ${formatRoleLabel(emp.role)}`}
    >
      <span className="ols-emp-avatar" aria-hidden>
        {String(emp.employee_name || "?").trim().slice(0, 2).toUpperCase()}
      </span>
      <span className="ols-emp-meta">
        <strong>{emp.employee_name}</strong>
        <em>
          {emp.employee_code ? `${emp.employee_code} · ` : ""}
          {formatRoleLabel(normalizeEmployeeRole(emp.role))}
        </em>
      </span>
    </Link>
  );
}

function DesignationBlock({ node, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const count = node.employee_count || 0;
  return (
    <div className={`ols-desig${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="ols-desig-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <Layers className="w-3.5 h-3.5 ols-desig-icon" />
        <span className="ols-desig-name">{node.name}</span>
        <span className="ols-count-pill">{count} people</span>
      </button>
      {open ? (
        <div className="ols-emp-grid">
          {(node.employees || []).length === 0 ? (
            <p className="ols-empty-inline">No employees on this designation here.</p>
          ) : (
            node.employees.map((emp) => <EmployeeChip key={emp.employee_id} emp={emp} />)
          )}
        </div>
      ) : null}
    </div>
  );
}

function DepartmentBlock({ node, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`ols-dept${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="ols-dept-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <Building2 className="w-4 h-4 ols-dept-icon" />
        <span className="ols-dept-name">{node.name}</span>
        <span className="ols-count-pill">{node.employee_count || 0} people</span>
        <span className="ols-count-pill ols-count-pill--soft">
          {node.designation_count || 0} designations
        </span>
      </button>
      {open ? (
        <div className="ols-dept-body">
          {(node.designations || []).length === 0 ? (
            <p className="ols-empty-inline">No designations with people in this department.</p>
          ) : (
            node.designations.map((d) => (
              <DesignationBlock
                key={d.designation_id || `none-${d.name}`}
                node={d}
                defaultOpen={(d.employee_count || 0) > 0 && (d.employee_count || 0) <= 8}
              />
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function LocationCard({ node, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const place = [node.city, node.state].filter(Boolean).join(", ");
  return (
    <article className={`ols-loc${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="ols-loc-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="ols-loc-icon" aria-hidden>
          <MapPin className="w-4 h-4" />
        </span>
        <span className="ols-loc-copy">
          <strong>{node.name}</strong>
          {place ? <em>{place}</em> : null}
        </span>
        <span className="ols-loc-stats">
          <span>{node.department_count || 0} depts</span>
          <span>{node.employee_count || 0} people</span>
        </span>
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>
      {open ? (
        <div className="ols-loc-body">
          {(node.departments || []).length === 0 ? (
            <div className="ols-loc-empty">
              <p>No employees allocated to this location yet.</p>
              <Link to="/settings/organization/branches">Manage branches</Link>
              <span aria-hidden>·</span>
              <Link to="/employees">Assign employees</Link>
            </div>
          ) : (
            node.departments.map((d) => (
              <DepartmentBlock
                key={d.department_id || `none-${d.name}`}
                node={d}
                defaultOpen={(d.employee_count || 0) > 0}
              />
            ))
          )}
        </div>
      ) : null}
    </article>
  );
}

export default function OrgLocationStructurePage() {
  const { role } = useContext(AuthContext);
  const isAdmin = hasRole(role, ["admin"]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError("");
    try {
      const res = await getOrgLocationStructure();
      setData(res);
    } catch (err) {
      setError(normalizeApiError(err) || "Could not load organisation structure");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  const filteredLocations = useMemo(() => {
    const list = data?.locations || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list
      .map((loc) => {
        const locHit =
          String(loc.name || "").toLowerCase().includes(q) ||
          String(loc.city || "").toLowerCase().includes(q) ||
          String(loc.state || "").toLowerCase().includes(q);

        const departments = (loc.departments || [])
          .map((dept) => {
            const deptHit = String(dept.name || "").toLowerCase().includes(q);
            const designations = (dept.designations || [])
              .map((desig) => {
                const desigHit = String(desig.name || "").toLowerCase().includes(q);
                const employees = (desig.employees || []).filter((emp) => {
                  const hay = `${emp.employee_name || ""} ${emp.employee_code || ""} ${emp.role || ""}`.toLowerCase();
                  return hay.includes(q);
                });
                if (locHit || deptHit || desigHit || employees.length) {
                  return {
                    ...desig,
                    employees: locHit || deptHit || desigHit ? desig.employees : employees,
                    employee_count:
                      locHit || deptHit || desigHit
                        ? desig.employee_count
                        : employees.length,
                  };
                }
                return null;
              })
              .filter(Boolean);
            if (locHit || deptHit || designations.length) {
              return { ...dept, designations };
            }
            return null;
          })
          .filter(Boolean);

        if (locHit || departments.length) {
          return { ...loc, departments };
        }
        return null;
      })
      .filter(Boolean);
  }, [data, search]);

  if (!isAdmin) {
    return (
      <PeoplePageShell
        eyebrow="People"
        title="Organisation structure"
        subtitle="Only Super Admin can view the location-wise organisation map."
        icon={Building2}
        showSwitch={false}
      >
        <div className="ols-denied">
          <p>This page is for Super Admin only.</p>
          <Link to={PEOPLE_PATHS.hierarchy} className="ph-h-primary-btn">
            Go to Reporting Hierarchy
          </Link>
        </div>
      </PeoplePageShell>
    );
  }

  const totals = data?.totals || {};
  const orgName = data?.organisation?.name || "Your organisation";

  return (
    <PeoplePageShell
      eyebrow="People"
      title="Organisation structure"
      subtitle="See how locations, departments, designations, and employees fit together — separate from who reports to whom."
      icon={Building2}
      showSwitch
      actions={
        <div className="ols-toolbar-actions">
          <Link to={PEOPLE_PATHS.hierarchy} className="ph-h-secondary-btn">
            <Network className="w-4 h-4" />
            Reporting hierarchy
          </Link>
          <button
            type="button"
            className="ph-h-ghost-btn"
            onClick={() => load(true)}
            disabled={refreshing}
          >
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      }
    >
      <div className="ols-page">
        <section className="ols-hero-card">
          <div className="ols-hero-copy">
            <p className="ols-kicker">Location-wise map</p>
            <h2>{orgName}</h2>
            <p>
              Flow: <strong>Organisation</strong> → <strong>Location</strong> →{" "}
              <strong>Department</strong> → <strong>Designation</strong> →{" "}
              <strong>Employee</strong>. Use this to see allocation gaps before you define reporting
              links.
            </p>
            <div className="ols-setup-links">
              <Link to="/settings/organization/branches">Branches</Link>
              <Link to="/settings/organization/departments">Departments</Link>
              <Link to="/settings/organization/designations">Designations</Link>
              <Link to="/employees">Employees</Link>
            </div>
          </div>
          <div className="ols-stats" aria-label="Structure totals">
            <StatPill icon={MapPin} label="Locations" value={totals.locations ?? "—"} />
            <StatPill icon={Building2} label="Departments" value={totals.departments ?? "—"} />
            <StatPill icon={Layers} label="Designations" value={totals.designations ?? "—"} />
            <StatPill icon={Users} label="Employees" value={totals.employees ?? "—"} />
          </div>
        </section>

        <div className="ols-filter-bar">
          <div className="ols-search">
            <Search className="w-4 h-4" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search location, department, designation, or employee…"
              aria-label="Search organisation structure"
            />
          </div>
        </div>

        {loading ? (
          <div className="ols-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span>Loading organisation structure…</span>
          </div>
        ) : error ? (
          <div className="ols-error">
            <p>{error}</p>
            <button type="button" className="ph-h-primary-btn" onClick={() => load()}>
              Try again
            </button>
          </div>
        ) : filteredLocations.length === 0 ? (
          <div className="ols-empty">
            <MapPin className="w-8 h-8" />
            <h3>{search ? "No matches" : "No locations yet"}</h3>
            <p>
              {search
                ? "Try a different search, or clear the filter."
                : "Add branches and allocate employees to see the full organisation map."}
            </p>
            {!search ? (
              <Link to="/settings/organization/branches" className="ph-h-primary-btn">
                Add locations
              </Link>
            ) : null}
          </div>
        ) : (
          <div className="ols-tree">
            {filteredLocations.map((loc, idx) => (
              <LocationCard
                key={loc.location_id || `unassigned-${idx}`}
                node={loc}
                defaultOpen={idx === 0 || (loc.employee_count || 0) > 0}
              />
            ))}
          </div>
        )}
      </div>
    </PeoplePageShell>
  );
}
